import { useEffect, useState, useCallback, useMemo } from 'react'
import { fetchRecordsUIDsByTemplateId } from '../services/entityInstanceServices'
import { EntityStatus, TemplatePermissionMode } from '../constants/constants'

interface UseEntityRecordsProps {
    templateId: string
    orgId: string
    id: string
}
interface IEntityUIDs {
    uid: string
    entityId: string
    templateId: string
    templateName: string
    attributes?: Record<string, any>
    hasItemEdit?: boolean
}

export const useEntityRecords = ({
    templateId,
    orgId,
    id,
}: UseEntityRecordsProps): {
    recordsList: IEntityUIDs[]
    isFetching: boolean
    hasMore: boolean
    hasAddNew: boolean
    onSearch: (_: string, text: string) => void
    fetchNextPage: () => void
} => {
    const [fetchedRecords, setFetchedRecords] = useState<IEntityUIDs[]>([])
    const [pageNumber, setPageNumber] = useState<number>(1)
    const [searchText, setSearchText] = useState<string>('')
    const [hasMore, setHasMore] = useState<boolean>(false)
    const [isFetching, setIsFetching] = useState<boolean>(false)
    const [hasAddNew, setHasAddNew] = useState<boolean>(false)
    const recordsList = useMemo(() => [...fetchedRecords], [fetchedRecords])
    useEffect(() => {
        if (!templateId || !orgId || !id) return

        const fetchRecords = async () => {
            setIsFetching(true)

            const records = await fetchRecordsUIDsByTemplateId(
                orgId,
                templateId,
                {
                    pageNumber,
                    pageSize: 30,
                    searchText: searchText.trim(),
                    filterBy: EntityStatus.ACTIVE,
                    includeUseOnly: true,
                    includeCommonOrg: true,
                    includeAttributes: true,
                },
            )

            const updated: IEntityUIDs[] = records?.data?.map((i: any) => ({
                ...i,
                hasItemEdit:
                    i.action === TemplatePermissionMode.WRITE ||
                    i.action === TemplatePermissionMode.READ_WRITE,
            }))

            setFetchedRecords((prev) =>
                pageNumber === 1 ? updated : [...prev, ...updated],
            )
            setHasMore(updated?.length === 30)
            setHasAddNew(
                records?.action === TemplatePermissionMode.WRITE ||
                    records?.action === TemplatePermissionMode.READ_WRITE,
            )
            setIsFetching(false)
        }

        fetchRecords()
    }, [templateId, orgId, id, pageNumber, searchText])

    const onSearch = useCallback((_: string, text: string) => {
        setSearchText(text)
        setPageNumber(1)
    }, [])

    const fetchNextPage = useCallback(() => {
        if (!isFetching && hasMore) {
            setPageNumber((p) => p + 1)
        }
    }, [isFetching, hasMore])

    return {
        recordsList,
        isFetching,
        hasMore,
        hasAddNew,
        onSearch,
        fetchNextPage,
    }
}
